// ボス定義データ。
// 設計のキモ (Phase 4.5):
//   - チュートリアル的ヒント (memoryHints) は廃止。「死にゲーは自分で気づくのが醍醐味」
//   - 攻撃継承システムは維持
// 🔴 廃止 (2026-07-29): loreFragments (死亡数で解放する世界観フレーバー)。
//   画面に出す経路が無く、先代の姫の分は白紙化済みの設定のまま残っていた。全文の退避先 =
//   `docs/departments/scenario/_archived/2026-07-29-lore-fragments-retired.md` (🔴参照禁止)。
//   世界観フレーバーの現行方針 = `docs/candidates/2026-07-29-achievement-flavor-text-design.md`

import type { BossContactMaterial, BossStandoffConfig, BossString } from './types'
import { EXECUTIONER_ATTACK_IDS, EXECUTIONER_UNLOCK_BY_EXECUTES, type ExecutionerHeld } from './executioner_weapon'
import { BOSS_CORE } from './balance'
import { buildAwokenFinaleTrial } from './awoken_finale_trial'
import { resolveHoundKit } from './hound_kit'
import { resolveKnightActs } from './knight_acts'

// 稽古の1課題 (チュートリアル 2026-07-05 リワーク・spec §2.1)。
//   goal 明示に一本化 (旧: 攻撃defから導出する二重導出を廃止)。
//   count 省略時は TUTORIAL.successesPerLesson (balance.ts)。
export type TutorialLesson = {
  attackId: string
  // exec_parry = 崩されて、ボスの決め技をパリィで弾く (配布版の稽古と DEV ?tutFocus=1|2|3 の課題で使う・src/tut_focus.ts)。
  // combo3 = 連撃の3段目を当てて、相手の体幹の戻りを止める (配布版の稽古に入る・DEV ?combo3rd=off の時だけ入らない・src/tut_focus.ts SHADE_COMBO3_LESSONS)。
  goal: 'parry' | 'just_parry' | 'guard' | 'counter' | 'zone_evade' | 'exec_parry' | 'combo3'
  count?: number
  // 稽古の掲示の見出し (「稽古 n / N ― 〇〇」の〇〇)。省略 = 出題する技の名前 (attacks.ts の label)。
  label?: string
  // この課題の間だけ、崩れた主人公へ出す決め技 (亡霊は普段は決め技を持たない・src/exec_parry.ts setTutorialFinisher)。
  finisherAttackId?: string
}

export type BossDefinition = {
  id: string
  name: string
  title: string
  role: string
  maxHp: number
  maxPosture: number
  baseAttackIds: string[]
  signatureAttackId: string
  // 気絶中のプレイヤーへの致命攻撃 (固有モーション)
  finisherAttackId?: string
  // トドメ致命の刺し距離 (px)。 未指定は EXECUTE.defaultFinisherAnchor(120)。 大型ボスほど大きめ (Codex 監査 C)。
  finisherAnchorOffset?: number
  // 画面に描かれた体の半幅（実測値）。scripts/measure_boss_body.py で測る。絵を差し替えたら測り直す。
  bodyHalfWidthPx?: number
  // ボスの「芯」(2026-09-06・docs/candidates/2026-09-06-boss-core-real-size-fable-design.md):
  //   歩き・見切り突進が止まる重心の半幅 (px)。未指定 = balance.ts BOSS_CORE.coreHalfWidth (28・全ボス共通値)。
  //   読む時は必ず bossCoreHalfWidth(boss) を通す (直接この値を読まない・§5-1)。
  coreHalfWidthPx?: number
  // 変形するボス (請われぬ介錯だけ) の形態別上書き。coreHalfWidthPx より優先する。
  //   例: 砲形態は「腕が武器でなく体」なので、胴より広い値を弾の生まれる位置から逆算して置く (設計書 §2-3)。
  coreHalfWidthPxByForm?: Partial<Record<ExecutionerHeld, number>>
  // 見切り反撃の突進・連撃3段目の踏み込みが止まる「ボス中心との距離」(px)。身体判定なし (BOSS_CORE.enabled=false) のまま、
  //   この距離より近くへは前進しない (歩き・回避は通り抜ける)。未指定 = 止めない (従来どおり 120px 滑って重なる)。
  //   読む時は bossCounterStopGap(boss) を通す。出所 = docs/research/2026-10-06-knight-overlap-evidence/README.md。
  lungeStopGapPx?: number
  // 間合いの立て直しの帯 (2026-10-06・ボス中心と主人公の中心の距離 px)。主人公がこれより近くに 0.15 秒居たら、次の待機で
  //   歩いて好みの間合いへ下がる (芯の帯より内側は必ず・外側は boss.ts overlapRegain.bandChance の確率)。
  //   未指定 = 芯の帯 (芯半幅+15＝ボス自身の踏み込みの止まり位置と同じ・従来どおり)。値 = 体が触れる距離 T。
  //   条件: 好みの間合い − 40 (下がり切る距離) より小さくする (超えると震える)。読む時は boss.ts overlapRegainBandFor を通す。
  //   出所 = docs/research/2026-10-06-overlap-audit/stage2-triage-and-design.md 案R。DEV ?regainBand=off|<px>。
  overlapRegainBandPx?: number
  // 背後滞在への応手を予約するまでの時間。未指定は共通の保守値。
  backAttackThresholdMs?: number
  // 物語の最終ボス (先代の姫) だけ true。 撃破で campaign clear (= 'cleared' 画面)。
  //   配役は段階実装ゆえ「配列末尾 ≠ finale」。 これが立つまで撃破は victory 止まり。
  isFinale?: boolean
  // チュートリアル無敵 (2026-06-11 ユーザー裁定): true のボス戦ではプレイヤーHPが0にならない
  //   (被弾しても最低1で止まる)。 体幹圧・被弾フリンチ・被弾SE は残す = 「失敗」は伝わるが死なない。
  //   導入戦 (名も無き亡霊) で「弾き/見切り/躱し」を安全に体に入れさせる。 id 直書きを避けるためのフラグ。
  protectFromDeath?: boolean
  // 致命 (処刑) を入れた回数で攻撃を段階解禁する (花嫁 spec §8)。
  //   未定義 = 常に baseAttackIds 全部。 定義時は atExecutes 昇順で ids を積み上げる。
  unlockByExecutes?: Array<{ atExecutes: number; ids: string[] }>
  // 解禁の流儀 (hollow-actor の前の版＝総集編 2026-06-11):
  //   'accumulate' (既定) = 段階を積み上げる (花嫁/騎士ほか)
  //   'replace'           = 最新の段階だけを使う (部屋ごとに「演目」が切り替わる)
  unlockMode?: 'accumulate' | 'replace'
  // 宵渡り (blink・起点技)。 unlockAtExecutes 回の致命以降、 クールダウンが明けるたび
  //   確率で発動し、 プレイヤーの反対側へ再出現して間合いと向きをリセットする。
  blink?: { unlockAtExecutes: number; cooldownMs: number }
  // 部屋制 (2026-06-11 協議): HPバーを区切る部屋の数。 未指定=1 (従来どおり)。
  //   部屋の境界は致命でしか越えられない。 教える量が多いボスほど部屋を増やす。
  roomCount?: number
  // 部屋HP不均等配分 (案X 2026-06-13): 部屋ごとの HP 取り分 (room順)。 length===roomCount で有効。
  //   例 [35,50,65] = 部屋1短く(学習)/部屋3長く(クライマックス)。 未指定=均等割り。
  roomHpFractions?: number[]
  // ボス別の歩き/間合いオーバーライド (Task 16・騎士以降のキャラ付け)。
  //   walkSpeedMul: BOSS.walkSpeed への倍率 (騎士の儀礼的な歩み ~0.55 など)。 未指定=1。
  //   preferredRange/attackCommitRange: BOSS 定数の差し替え (槍ボスは遠くから攻撃に入る)。
  walkSpeedMul?: number
  preferredRange?: number
  attackCommitRange?: number
  // 最終部屋 (executesTaken >= roomCount-1) で攻撃間の待ち時間に掛ける倍率 (<1 = テンポUP)。
  //   クライマックス用。 未指定=1 (変化なし)。 予告時間は縮めない (読みやすさは保つ)。
  finalRoomTempoMul?: number
  // 攻撃間の「間」(idle gap) への per-boss 倍率 (2026-07-07 覚醒王)。 >1 = 間を作る=メリハリ。
  //   長い間の緊張は retaliationAttackId (連打への応手) が担保する (SEKIRO の「アイドルは長いが
  //   先制すると刺される」テクスチャ)。 未指定=1。
  idleGapMul?: number
  // 攻撃後の「次の一手」= 生きたお見合い (2026-07-19)。
  //   未指定 = press 100% = 現行挙動と完全一致。 ボスごとに重みを振った分だけ変わる。
  //   設計: docs/candidates/2026-07-19-boss-living-standoff-design.md
  standoff?: BossStandoffConfig
  // 最終部屋でのボス体幹ゲイン倍率 (2026-06-13): <1 で崩しに必要な弾きが増える=クライマックスを延ばす。
  //   未指定=1。 部屋を閉じるのは崩し→致命なので、 最終部屋の戦闘長はこれが本命 (HP配分では効かない)。
  finalRoomPostureMult?: number
  // 稽古モード (チュートリアル 2026-07-05 リワーク): 定義順に1課題ずつ出題し、 goal 明示の
  //   成功条件 (count 回・省略時 TUTORIAL.successesPerLesson) を満たすたび次の課題へ。
  //   全部終えるとボスが仕上げ (本物の経済=ジャストパリィで崩す) に入る (進行=game.ts)。
  tutorialLessons?: TutorialLesson[]
  // 割り込み反撃 (combat-feel-v2 §4.1): 短時間に殴られ続けた時、 idle を打ち切って出す技。
  //   未指定 = 反撃しない。 各ボス着手時に設定する (第1陣 = 騎士/処刑人)。
  retaliationAttackId?: string
  // フェーズ移行技 (2026-07-03 導入・処刑人 G11): HP がしきい値以下になった瞬間 1 回だけ強制発動する
  //   特殊技。 「咆哮→突進」型の状態変化マーカー。 発動後 boss.phaseTransitionTriggered=true でロック。
  //   ⚠️ 両方指定が必須 (attackId 単独では発動しない)。 hpThreshold=0.6 → maxHp*0.6 で発動 (=HP 60%)。
  //   idle → 次の技を選ぶ瞬間に判定。 telegraph 中の技は中断しない (途中で乗っ取らない)。
  phaseTransitionAttackId?: string
  phaseTransitionHpThreshold?: number
  // 部屋突入で確定初手 (2026-07-10 導入・花嫁「裾乱れ」用): phaseTransitionAttackId の
  //   executesTaken駆動版。 room = executesTaken の値。 その値に達した idle→telegraph 遷移の
  //   瞬間、次の攻撃を attackId へ強制上書きする (1 room = 1 回だけ・boss.roomEntryForcedDone でロック)。
  //   phaseTransition (HP駆動) と違い、致命回数=部屋制のボス (花嫁/騎士等) 向け。
  roomEntryForcedAttacks?: Array<{ room: number; attackId: string }>
  // 背後応手 (combat-feel-v2 §4.3): 攻撃中に背面へ X ms 滞在されたら recovery 明けに出す
  //   振り向き技。 未指定 = 応手なし。
  backAttackId?: string
  // Optional act-specific rear response; default keeps existing turn-toward-player behavior.
  backAttackByExecutes?: { atExecutes: number; attackId: string; facePlayerWithRear?: boolean }[]
  // 背後の応手を続けて出してよい回数 (2026-10-10)。未指定 = 上限なし (今まで通り)。
  //   1 = 応手の隙が終わったら、まだ背中側にいても必ず待機へ戻って向き直る。
  //   出どころ = 戴冠獣の尾の薙ぎ (背中を主人公へ向けて出す) が自分の溜めの間に背面滞在を積み、永久に続いた
  //   (ユーザー 10-10「一度戴冠獣の背後を取ると背後攻撃を連発し続ける」・tests/hound_back_chain.test.ts)。
  backAttackMaxChain?: number
  // P2 経済 (ブループリント §4.1): 非攻め時の melee 被弾の受け方。
  //   'weapon' (人型武器持ち=騎士/処刑人/剣舞/先代の姫) / 'veil' (花嫁) / 'none' (亡霊/王=獣)。
  //   未指定 = none (受けない=従来通り chip が通る=序盤の素直な進行カーブ)。
  meleeGuard?: 'weapon' | 'veil' | 'none'
  // 生身へ通常攻撃が触れた時の材質。省略時は flesh。
  contactMaterial?: BossContactMaterial
  // P3 string エンジン (ブループリント §3.1): 定義時は attackIds プールを置き換えて
  //   「連」で攻める。 未指定 = 従来の単技パス (baseAttackIds / pickNextAttack)。
  strings?: BossString[]
  // DEV ?altAttacks の直接指定だけが引く連 (2026-10-08 戴冠獣の三連を技から外した時に追加)。
  //   通常の抽選・解禁・連の単独化 (soloString) には一切出ない。boss.ts の altStringFor が strings の後に探す。
  altOnlyStrings?: BossString[]
}

// 配役の正本 = docs/departments/scenario/cast-roster-master.md。
//   旧2体 (neon-harpy / masked-acrobats = 遊園地/サーカス路線) は 2026-06-04 に完全退役
//   (正典 spec 2026-06-03 に存在しない旧路線。 履歴は git に保全)。
//   新配役 (花嫁/騎士/処刑人/覚醒王/道化/剣舞/千日手の軍配者たち/先代の姫) は 1 体ずつ別 Plan で追加する。
//   (旧記述「実装済ボスは王のみ」は 2026-06 時点。各ボスの制作状況は docs/boss-production-map.md を参照)
export const BOSS_DEFINITIONS: BossDefinition[] = [
  {
    // 正典 spec §7.0「名も無き亡霊」= チュートリアル (導入戦)。
    //   遅く弱く優しい。 新3文法 (弾き / 短い避け→反撃 / 長い位置どり) を4攻撃で一通り
    //   出題して操作を体に入れさせる (hit-timeline 2026-06-11)。
    //   finisher なし = 体幹を崩されても処刑は来ない (導入の優しさ)。
    //   例外は稽古の「崩されて弾く」課題の間だけ (課題ごとに決め技 shadeExecution を出す・src/tut_focus.ts)。
    id: 'nameless-shade',
    meleeGuard: 'none',   // 稽古の亡霊: 受けない (序盤は素直に殴れる教室)
    name: '名も無き亡霊',
    title: 'The Nameless Shade',
    role: '稽古の相手',   // 物語の役どころは付けない (台帳 story.md 47回目・10-26 作者「ただのチュートリアルのつもり」)
    maxHp: 40,
    maxPosture: 60,
    roomCount: 1,   // 崩す→致命、 の1周を体験する教室
    protectFromDeath: true,   // チュートリアル無敵: 死なずに新3文法を覚える教室
    // 「遅く弱く優しい」稽古相手。 未設定だと 270px/s = 騎士の1.8倍で、教室らしくなかった
    //   (2026-07-20 実機A/B: 今の速さ vs 0.6 を比べてユーザーが 0.6 を採用 ≒ 162px/s)。
    walkSpeedMul: 0.6,
    // 重なり (2026-10-06 ユーザー裁定 Q1「体が食い込む重なりだけ直す」Q3 反撃の止まり位置を入れる)。
    //   見切り反撃・連撃3段目は亡霊の中心から 120px 手前で止まる (胴の前端 37 ＋ 剣先 100 − 刺さり約17・試す幅 110〜130)。
    //   立て直しの帯 59 = 体が触れる距離 T (段階1の実測)。ボスが自分の踏み込みで止まる 43px を含む＝ときどき下がる。
    //   出所 = docs/research/2026-10-06-overlap-audit/stage2-triage-and-design.md。旧 = DEV ?counterStop=off&regainBand=off。
    lungeStopGapPx: 120,
    overlapRegainBandPx: 59,
    baseAttackIds: ['shadeSlash', 'shadeThrust', 'shadeGrab', 'shadeRipple'],
    // 旧稽古の6課題 (いまは DEV ?tutFocus=0 の時だけ使う)。
    //   配布版の稽古はこの並びではなく src/tut_focus.ts の SHADE_COMBO3_LESSONS
    //   (弾き → 受け → 崩されて弾く → 連撃の3段目 → 躱して反撃(突き) → 位置どり)。tutorialLessonsFor が差し替える。
    //   ここの「完全な弾き(just_parry)」はパリィを1種類にした (10-09) 後はどのパリィでも数える＝ただの弾きと同じ (src/tutorial_cards.ts の注記)。
    //   → 仕上げ (本物の経済で体幹を割ると崩れる。 game.ts の自動stagger削除済)。
    tutorialLessons: [
      { attackId: 'shadeSlash', goal: 'parry' },
      { attackId: 'shadeSlash', goal: 'just_parry', count: 2 },
      { attackId: 'shadeSlash', goal: 'guard' },
      { attackId: 'shadeThrust', goal: 'counter' },
      { attackId: 'shadeGrab', goal: 'counter' },
      { attackId: 'shadeRipple', goal: 'zone_evade' },
    ],
    signatureAttackId: 'shadeSlash',
  },
  {
    // 正典 spec §7.1「王 (戴冠獣) ― 支配の恐怖」。 id は据え置き (スプライト/BGM/セーブ波及回避)。
    //   2026-07-05 boss-slice (王クローズ): 呼称を「番獣 (guardian)」→「王=退位できぬ
    //      支配者」トーンへ再フレーム済 (ユーザー裁定 name=灰の戴冠獣)。ビジュアル/id/攻撃は据え置き。
    id: 'dream-hound',
    meleeGuard: 'none',   // 王=獣: 受けない (殴り得は機動側で締める)
    contactMaterial: 'flesh_chain', // 灰の毛皮・肉と、王冠から垂れる鎖の複合接触
    name: '灰の戴冠獣',
    title: 'The Undethroned',
    role: '玉座を離れられぬ、灰の王城の主',
    maxHp: 100,
    maxPosture: 100,
    roomCount: 2,   // 2幕。実効の体力・ゲージは econ.ts bossPools (10-08 採用の段 c：体力 240＝幕ごと 120・ジャスト約 7〜8 回で1幕が崩れる＝bot 実測)
    baseAttackIds: ['houndBite', 'houndLunge'],
    signatureAttackId: 'houndLunge',
    finisherAttackId: 'houndExecution',
    // 体の半幅 + 約60。絵を差し替えたら bodyHalfWidthPx と一緒に見直す。
    finisherAnchorOffset: 210,
    bodyHalfWidthPx: 152,
    lungeStopGapPx: 185,        // ① 見切り反撃・連撃3段目が戴冠獣の中心から 185px 手前で止まる（試す幅 170〜200）
    overlapRegainBandPx: 118,   // R 立て直しの帯＝体が触れる距離 T（頭の塊）。ときどき 40% 次の待機で歩いて下がる
    backAttackThresholdMs: 80,
    backAttackId: 'houndBite',
    // 背後の応手は1回で切る (2026-10-10)。戴冠獣は基本操作を学ばせる役 (侍大将)＝背中を取った罰は1回見せれば足りる。
    //   2回目以降は「背中を取ったのに何もできない」になるだけで、教えることが増えない。応手の後は待機で向き直り、普段の技か間合いの立て直しへ。
    //   旧 (続けて出す) = DEV ?houndBackChain=old (boss.ts setBackAttackChainCap)。
    backAttackMaxChain: 1,
    // 立ち位置 (2026-08-11 ユーザー実プレイ「ほとんどの時間でプレイヤーとボスの顔あたりが被ってる」)。
    //   覚醒王で 2026-08-10 に直したのと**同じ症状・同じ原因**。王も四つ足で横に長く、頭が前へ
    //   大きく張り出すのに、共通既定の preferredRange 150 のままだった (覚醒王だけ直して王は
    //   置き去りになっていた)。
    //   実測 = idle の絵は実表示 303.4px 幅 → 頭は中心から前へ約152px 張り出す (bodyHalfWidthPx 152
    //   と一致)。プレイヤー半幅15px を足すと、旧 150 では**隙間がマイナス**＝顔の中に立っている。
    //   200 なら隙間 33px (覚醒王の 200 と同じ考え方・同じ数値)。
    //   🔴 この2つは必ずセットで動かす: preferredRange < attackCommitRange でないと、
    //      詰め切った位置が「攻撃を出してよい間合い」の外になり、ボスが一切攻撃しなくなる。
    //   攻撃の届き = 噛み range145+踏み込み140 = 285 / 突進牙 range140+踏み込み260 = 400。
    //      どちらも commitRange 210 を超えるので、下がっても届く。
    //   ⚠️ 王は大暴れのような床の帯を持たないので、覚醒王にあった hazard_escape の連動制約は無い。
    //   ⬜ 体感はユーザーの実プレイ待ち。戻すならこの2行を消すだけで既定 (150/185) に戻る。
    preferredRange: 200,
    attackCommitRange: 210,
    idleGapMul: 1.9,
    // 歩く速さ (2026-11-04 作者 slow・db answers/hound-walk-8f-1104)。270×0.75 = 202.5px/s・8コマの1周 180.5px ≒ 0.89 秒。
    walkSpeedMul: 0.75,
  },
  {
    // 第3ボス「主なき近衛騎士」(ユーザー設計たたき台 2026-06-11)。
    //   テーマ = 忠誠心と否認。 もういない王の寝室を守り続ける半壊の近衛。
    //   戦闘の主題 = 「間合い」と「攻め時」。 槍 = 境界線。
    //   - ゆっくり間合いを詰める「近づいてくる境界線」(walkSpeedMul 0.55 ≒ 148px/s。
    //     プレイヤー後退 120px/s より速い = 逃げ続けても必ず間合いが来る)
    //   - 槍の遠間 (preferredRange 230) から攻撃に入る。 突進ボスではない
    //   - 部屋1: 突き(見切り)+薙ぎ(弾き) → 部屋2: 石突の宣告(懐へ) → 部屋3: 二段突き(我慢)
    id: 'vigil-knight',
    bodyHalfWidthPx: 48,
    // 反撃の剣先は主人公の中心から前へ約100px (2026-10-06 実画面で計測)。騎士の胴の前端は中心の約30px手前なので、
    //   110px で止めると剣先が胴へ15〜20px刺さる位置になり、体どうしは重ならない (実プレイ記録 12/12 の反撃が終点 0〜71px だった)。
    lungeStopGapPx: 110,
    backAttackThresholdMs: 100,
    backAttackId: 'lanceSweep',
    meleeGuard: 'weapon',   // 近衛騎士: 槍で受ける (金属音+火花)
    name: '主なき近衛騎士',
    title: 'The Vigil Knight',
    role: '誰もいない王の寝室を守り続ける近衛',
    maxHp: 150,
    maxPosture: 110,
    roomCount: 3,
    // 案X (2026-06-13 ユーザー裁定): 部屋1短く(突きの読みを孤立学習)→部屋3長く(苛烈な読み合いの本編)。
    //   ※ここと下の finalRoomPostureMult・maxHp/maxPosture は v1 の素の値。実効値は econ.ts の bossPools が上書きする
    //     (docs/generated/econ-current-values.md を参照)。
    roomHpFractions: [35, 50, 65],
    finalRoomTempoMul: 0.82,   // 部屋3=クライマックス: 攻撃の回転だけ上げる (予告=読みやすさは保つ)
    // 部屋3=本編: 崩しを遅らせて戦闘を約2倍に (ユーザー裁定 2026-06-13「倍くらい戦闘してもいい」)。
    //   部屋1=突き中心で弾けない=HP削り進行ゆえ、 これを下げても部屋1は短いまま (両立)。
    finalRoomPostureMult: 0.5,
    baseAttackIds: ['lanceThrust', 'lanceSweep', 'lanceButt'],
    signatureAttackId: 'lanceThrust',
    finisherAttackId: 'knightExecution',
    finisherAnchorOffset: 150,   // 槍の刺し = telegraph 中に寄せる目標距離 (player.x ± この値に停まり刺す)
    walkSpeedMul: 0.55,
    // 🔵 2026-09-27 230/280 → 175/215: 突き・薙ぎの当たりを描かれた穂先まで縮めた (128/95・src/knight_reach.ts) ぶん、
    //   同じ遠さから振ると踏み込み (振り終わりの絵のまま滑る距離) が約120px伸びた (棒立ち相手の突き 60→179px)。
    //   着地の差 (突き 222→100 / 薙ぎ 172→67) だけ手前まで歩かせ、踏み込みを旧と同程度に戻す。175 は主人公の通常攻撃の届き 160 の外。
    //   ⚠️ 壁際の薙ぎ下がり (WALL_BAND 200) に入る頻度が上がる (攻め回避bot 1.0→3.3回/戦)。旧値は DEV ?knightReach=old。
    preferredRange: 175,
    attackCommitRange: 215,
    retaliationAttackId: 'lanceSweep',   // 張り付き連打への応手 = 儀礼的な「払い」(§4.1)
    // P3 string (ブループリント §3.4): 槍の連。 全拍とも既存の lance 攻撃を組み替えただけ (新規絵ゼロ)。
    //   G-3 (2026-06-13): 部屋解禁を実装。 部屋1=突き(回避)+薙ぎ(パリィ) の 2択 (裁定A: 読みを2種) →
    //   部屋2=2連/3連 (リズムの読み) → 部屋3=フェイント連 (隙の真贋/我慢)。 ※騎士=ユーザー設計に
    //   つき配分は提案ベース・確定は H-1 審判で (unlockAtExecutes を動かすだけで段階を変えられる)。
    strings: [
      // 宣告 (1拍・常時): 見切り → 大きな隙。 部屋1の「突き=回避」の読み。
      { id: 'knight-proclamation', weight: 1.0, unlockAtExecutes: 0, beats: [{ attackId: 'lanceThrust' }] },
      // 境界の薙ぎ下がり (1拍・部屋2〜・壁際専用): 壁際から横薙ぎしつつ中央へ退く位置リセット技。
      //   requiresBossNearWall:true = 壁際(端から200px以内)で最優先発動。 弾ける (赤オーラなし)。
      //   target = アリーナ中央 (反対端まで行かない)。 recovery 1250ms = 中央で長い隙 → 弾いた報酬が明確。
      //   C-6 ロックアウト: 連発防止 (CROSS_ARENA_LOCKOUT_ATTACKS=3回分は別 string を挟む)。
      { id: 'knight-threshold-cross', weight: 1.1, unlockAtExecutes: 1, requiresBossNearWall: true, requiresPlayerWallSide: true, beats: [{ attackId: 'lanceCross' }] },
      // 柄の押し返し (2026-10-01・既定の「近い時の薙ぎの差し替え」)。抽選には出ず、boss.ts の差し替えだけが選ぶ (knight-threshold-cross と同じ解禁)。
      { id: 'knight-threshold-shove', weight: 0, unlockAtExecutes: 1, beats: [{ attackId: 'lanceShove' }] },
      // 境界の払い (1拍・常時): 弾き。 部屋1の「薙ぎ=パリィ」の読み。
      //   裁定A (2026-06-13 ユーザー実機): 部屋1が「突きだけ」で読み不在=単調だった
      //   (退屈の正体は読みの数ゼロ)。 弾ける薙ぎを足して 突き(回避)/薙ぎ(パリィ) の 2択にする。
      //   ※この配分変更はユーザー裁定。 design 意図 (line 180「部屋1: 突き+薙ぎ」) とも一致。
      { id: 'knight-ward-sweep', weight: 1.0, unlockAtExecutes: 0, beats: [{ attackId: 'lanceSweep' }] },
      // 境界の払い (3拍): 弾き・弾き・見切り。 中間拍は短い溜め、 締めは深い隙 (審判の本体)。 部屋2〜。
      { id: 'knight-boundary-sweep', weight: 1.3, unlockAtExecutes: 1, beats: [
        { attackId: 'lanceSweep' },
        { attackId: 'lanceSweep', gapMs: 450, chargeOverrideMs: 420 },
        { attackId: 'lanceThrust', gapMs: 500, chargeOverrideMs: 520, recoveryOverrideMs: 1150 },
      ] },
      // 払いからの咎め (2拍): 弾き → 離れ。 至近に張り付く相手を狩る分岐。 部屋2〜。
      { id: 'knight-sweep-punish', weight: 0.8, preferClose: true, unlockAtExecutes: 1, beats: [
        { attackId: 'lanceSweep' },
        { attackId: 'lanceButt', gapMs: 400, chargeOverrideMs: 500 },
      ] },
      // 欺きの突き (3拍): 弾き → 【見切りの突き=連の"途中"の危険ビート】 → 弾き締め。 部屋3 (クライマックス)。
      //   弾き連打のまま進むと2拍目の突きをパリィしようとして空振り→被弾+空振り罰=自滅。
      //   = blueprint §3.2.4「弾いてはいけない一拍」を連の中央に置く版。 連打が最適解にならない錨。
      { id: 'knight-feint-thrust', weight: 1.0, unlockAtExecutes: 2, beats: [
        { attackId: 'lanceSweep' },                                            // 弾き (初段=読ませる)
        { attackId: 'lanceThrust', gapMs: 380, chargeOverrideMs: 520 },        // 見切り (途中の危険ビート=回避)
        { attackId: 'lanceSweep', gapMs: 400, chargeOverrideMs: 450, recoveryOverrideMs: 900 }, // 弾き締め+深め隙
      ] },
      // 境界の宣告・五連 (混合5連): 弾き弾き→【見切り突き】→弾き→【見切り突き締め】。 部屋3クライマックス。
      //   parry↔mikiri を連の中で切り替える最難ビート。 弾くのは3回=体幹+75 (溢れない・自動崩しにしない)。
      //   ユーザー裁定 2026-06-13: 「突きとパリィ可能を混ぜた5連」。
      //   weight 0.9→1.5 (2026-06-21 ユーザー裁定: 部屋3で3択5連の出現割合を上げる)。 H-1 で要調整。
      { id: 'knight-boundary-five', weight: 1.5, unlockAtExecutes: 2, beats: [
        { attackId: 'lanceSweep' },                                                          // 弾き (リズム導入)
        { attackId: 'lanceSweep', gapMs: 400, chargeOverrideMs: 430 },                       // 弾き (乗せる)
        { attackId: 'lanceThrust', gapMs: 380, chargeOverrideMs: 500 },                      // 見切り (連中央の罠)
        { attackId: 'lanceSweep', gapMs: 420, chargeOverrideMs: 430 },                       // 弾き (引き戻す)
        { attackId: 'lanceThrust', gapMs: 400, chargeOverrideMs: 520, recoveryOverrideMs: 1100 }, // 見切り締め+深い隙
      ] },
      // 五連の払い (純パリィ5連): 薙ぎ×5。 全部弾き切る純パリィの見せ場。 部屋3クライマックス。
      //   parryPostureMul 0.65 = 5×25×0.65≒81/110 = 大報酬だが自動崩しにはしない (溢れ防止・ユーザー指摘)。
      //   ユーザー裁定 2026-06-13: 「全部弾ける純パリィ5連も欲しい」。
      { id: 'knight-five-sweep', weight: 0.7, unlockAtExecutes: 2, parryPostureMul: 0.65, beats: [
        { attackId: 'lanceSweep' },
        { attackId: 'lanceSweep', gapMs: 360, chargeOverrideMs: 420 },
        { attackId: 'lanceSweep', gapMs: 360, chargeOverrideMs: 400 },
        { attackId: 'lanceSweep', gapMs: 360, chargeOverrideMs: 400 },
        { attackId: 'lanceSweep', gapMs: 380, chargeOverrideMs: 420, recoveryOverrideMs: 950 }, // 締め+深め隙
      ] },
    ],
    unlockByExecutes: [
      { atExecutes: 0, ids: ['lanceThrust', 'lanceSweep'] },  // K-1: 幕1から間合いの出題
      { atExecutes: 1, ids: ['lanceButt'] },                   // K-2: 密着咎め
    ],
  },
  {
    // 正典 spec §7.2「待宵の花嫁」= 回避を教えるボス。
    //   詳細設計 = docs/superpowers/specs/2026-06-11-bride-evade-boss-design.md (回避ボス版)。
    //   攻撃キット: 弾き1 (veilSweep) + 回避3 (位置よけ/躱し/位置取り)。 致命回数で段階解禁 (§8)。
  //   ★2026-06-11 役割整理: 花嫁=回避/位置よけ、 見切り(回避反撃)は騎士の役 → 花嫁から反撃窓を全廃。
  //     (2026-10-10 注: 今は全ボス共通で「弾けない近接を回避の無敵でくぐれば見切りが開く」(hits.ts) ので、花嫁の赤でも開く。
  //      10-06 の重なりの直しで花嫁にも lungeStopGapPx を入れてあり、開くことは前提になっている。上の「全廃」は旧い記述)
    //   ★2026-06-11 流れ修正 (プレイテスト): maxHp 180→150 / 部屋 4→3 / 宵渡り 致命3→1。
    //     旧構成は宵渡り(間合いリセット)が最終部屋でしか出ず、 そこまでボスが前進し続けて
    //     プレイヤーが片側の壁にドリフトする「一方的に攻められ続ける」感触だった。 宵渡りを
    //     2部屋目から出してリセットを早め、 部屋を3に縮めて壁ドリフトが残る区間 (1部屋目) を短縮。
    //     50HP×3 = 王と同じ刻み・HP150 = 騎士と同じ (ゲーム全体の一貫値)。
    id: 'bride',
    bodyHalfWidthPx: 61,
    backAttackThresholdMs: 100,
    backAttackId: 'veilSweep',
    meleeGuard: 'veil',   // 花嫁: ヴェールが刃を受ける (布の音と視覚)
    name: '待宵の花嫁',
    title: 'The Bride of the Waiting Eve',
    role: '灰の王城で婚礼を待ち続ける花嫁',
    // 2026-07-01 難易度上げ (ユーザー実プレイで「ノーダメ1分クリア」 = 弱すぎ指摘):
    //   maxHp 150→210 (+40%): 一通り攻撃見る前に倒せない戦闘量に。 3 で割り切れる (70×3 = 端数なし)。
    //   maxPosture 100→130 (+30%): パリィ蓄積での崩し頻度を抑え、 崩し前に攻撃選択の読み合いが増える
    //   ※ランタイム実効値は econ.ts の bossPools が上書き (実効値は docs/generated/econ-current-values.md を参照)
    maxHp: 210,
    maxPosture: 130,
    // 重なり (2026-10-06 ユーザー裁定 Q1・Q3。亡霊と同じ直し)。
    //   見切り反撃・連撃3段目は花嫁の中心から 130px 手前で止まる (胴の前端 47〜51 + 腕・裾の張り出し。試す幅 120〜135)。
    //   立て直しの帯 62 = 体が触れる距離 T (胴 47 + 15)。連で続けて踏み込む技が多く、効きは小さい見込み (段階2)。
    //   招待・致命の抱擁 (懐へ入るのが正解の技) は直さない。連の踏み込みを手前で止める案T は比較版で選ぶ (保留)。
    //   出所 = docs/research/2026-10-06-overlap-audit/stage2-triage-and-design.md。旧 = DEV ?counterStop=off&regainBand=off。
    lungeStopGapPx: 130,
    overlapRegainBandPx: 62,
    roomCount: 3,   // 部屋 = 70HP ×3。 致命のたびに役 (技) が一段進む
    baseAttackIds: [
      'veilSweep', 'veilInvitation', 'vowEmbrace', 'thornAisle', 'veilFlurry',
      'veilSweepLink', 'veilSweepPerilous', 'veilBlinkTriple',
    ],
    signatureAttackId: 'veilSweep',
    finisherAttackId: 'brideExecution',
    // 致命を入れるたび「単語→切替文→最終試験」へ進む。
    //   attackPoolForBoss はDEV単技経路の解禁表。通常戦闘の排他は strings の maxExecutes/blinkOnly が正。
    unlockByExecutes: [
      { atExecutes: 0, ids: ['veilSweep', 'veilInvitation', 'vowEmbrace'] },
      { atExecutes: 1, ids: ['veilSweepLink', 'veilBlinkTriple'] },
      { atExecutes: 2, ids: ['thornAisle', 'veilFlurry', 'veilSweepPerilous'] },
    ],
    // 裾乱れ (veilFlurry) = 3部屋目 (executesTaken=2) 突入で確定初手・1回だけ (2026-07-10 導入)。
    //   設計正本: docs/candidates/2026-07-10-bride-evasion-rescope-flurry.md §3。
    roomEntryForcedAttacks: [{ room: 2, attackId: 'veilFlurry' }],
    // 致命1回後 (2部屋目) から宵渡り解禁: 間合いをリセットして壁ドリフトを断つ。
    //   新技は増やさず、 既存の技を別位置から再出題する (spec §8)。
    //   2026-07-01: cooldown 8000→3000ms + 常時減衰化 (boss.ts) で実プレイで頻度感じるように。
    //     旧 5000ms は idle 制限で実効 30-60s = 「1〜2 回しか見てない」 指摘 (ユーザー実プレイ)。
    blink: { unlockAtExecutes: 1, cooldownMs: 3000 },
    // 2026-07-18: 「回避の先生」を維持しつつ、避け→弾きへ守り方を切り替える三幕へ再構成。
    //   P1=基本3技を単独教育 / P2=避け→弾きの2拍＋ワープ1/3撃 / P3=床・大技・3拍を複合。
    strings: [
      // P1: 大技・床・ワープなし。3単語を単独で教え、致命1回後は通常候補から退役。
      { id: 'bride-p1-sweep', weight: 1.2, maxExecutes: 0, beats: [{ attackId: 'veilSweep' }] },
      { id: 'bride-p1-invitation', weight: 1.1, maxExecutes: 0, beats: [{ attackId: 'veilInvitation' }] },
      { id: 'bride-p1-embrace', weight: 1.0, maxExecutes: 0, beats: [{ attackId: 'vowEmbrace' }] },
      // P2以降: 避ける拍のactive終了後、300msの間＋350msの銀の振りかぶりを必ず置く。
      // 2026-07-24: 距離ゾーン補正(preferClose/Mid/Far)を花嫁から全撤去。近接ゲーでは戦いが至近固定になり
      //   preferMid/Far は構造的に発火せず、preferClose だけが実効=技出現が偏る(星試験が近距離で約1.4%まで沈む)。
      //   非大技は均等重み(1.0)で満遍なく、大技(裾乱れ)だけ控えめ、が新方針。詳細は当日ユーザー裁定。
      { id: 'bride-p2-invitation-link', weight: 1.0,
        unlockAtExecutes: 1, beats: [
          { attackId: 'veilInvitation' },
          { attackId: 'veilSweepLink', gapMs: 300 },
        ] },
      { id: 'bride-p2-embrace-link', weight: 1.0,
        unlockAtExecutes: 1, beats: [
          { attackId: 'vowEmbrace' },
          { attackId: 'veilSweepLink', gapMs: 300 },
        ] },
      // 2026-07-19: P2の弾ける拍が全て350msになるのを崩し、速い/遅いの混在をP2内へ持ち込む。
      { id: 'bride-p2-sweep', weight: 0.8, unlockAtExecutes: 1, maxExecutes: 1,
        beats: [{ attackId: 'veilSweep' }] },
      // ワープ後だけの1撃／3撃。通常抽選へ混ぜず、blink中に先に予約する。
      { id: 'bride-blink-single', weight: 1.0, unlockAtExecutes: 1, blinkOnly: true,
        beats: [{ attackId: 'veilSweepLink' }] },
      { id: 'bride-blink-triple', weight: 1.0, unlockAtExecutes: 1, blinkOnly: true,
        beats: [{ attackId: 'veilBlinkTriple' }] },
      // P3: 床/大技/3拍を複合。距離タグ撤去(上記2026-07-24)により近距離でも均等に出る。
      //   非大技(茨の床/星試験)は均等1.0。大技=裾乱れ(veilFlurry)は部屋突入で1回確定発動する
      //   (roomEntryForcedAttacks)ため、ランダム枠は控えめ0.5=「たまに追加で来る特別な一撃」に留める。
      { id: 'bride-p3-thorns-link', weight: 1.0, unlockAtExecutes: 2, beats: [
        { attackId: 'thornAisle' },
        { attackId: 'veilSweepLink', gapMs: 300 },
      ] },
      { id: 'bride-p3-invitation-flurry', weight: 0.5, unlockAtExecutes: 2, beats: [
        { attackId: 'veilInvitation' },
        { attackId: 'veilFlurry', gapMs: 350 },
      ] },
      // 🔴 撤去 (2026-07-26 ユーザー裁定): 'bride-p3-switch-triad'
      //   = veilSweep → veilSweepPerilous(赤) → veilSweepLink の「弾き→回避→弾き」3拍連。
      //   友達プレイテストで「攻撃モーションが速すぎて何をしているか見えない」と指摘された技。
      //   出所と全経緯 = docs/playtest/2026-07-26-friend-feedback-triage.md §5-5 / §8-1。
      //   理由:
      //     ① veilSweepPerilous は単独初登場が無く、この連の中段でしか出ない = 教える順番が抜けていた。
      //        しかも予告350msで前後を弾ける拍に挟まれる = 構造的にいちばん厳しい位置。
      //     ② 真因は速さより絵。 予告絵が veil_open (= 直前に弾いた veilSweep の当たり絵) の使い回しで、
      //        弾ける派生 veilSweepLink には専用予告絵 veil_link_snap_windup があるのに、
      //        弾けない派生には無い非対称。 2026-07-19 の「予告の3条件」確立前に発注された技。
      //     ③ 消しても花嫁の「歩いて避ける」教育は無傷。 veilSweepPerilous は幅270pxの帯で
      //        stepDistance 90px では出られず、 位置どり技ではない (正解は無敵タイミングのみ)。
      //        位置どりは veilInvitation (幕1で単独初登場) と thornAisle が担う。
      //     ④ 失われる「弾きの拍に赤を1拍混ぜる切替ドリル」は騎士が先に2種で教えている
      //        (knight-feint-thrust / knight-boundary-five)。 v0.5 の順は 亡霊→王→騎士→花嫁。
      //   ⚠️ 技そのものは残す (AGENTS.md §6 既存資産は削除しない): AttackDef (attacks.ts)・
      //     ポーズ定義 (boss_pose_timeline.ts)・baseAttackIds・unlockByExecutes は不変なので
      //     DEV単技経路では今も再生できる = 専用予告絵を与えてから復活させられる休眠資産。
    ],
    // 最終部屋 (3部屋目) で攻撃テンポを上げる (クライマックス・プレイテスト要望 2026-06-11)。
    //   待ち時間 (旧 820-1550ms) を短縮。 予告(溜め)は縮めない = 反応の床は不変
    //   ＝ここがクリア可能性の担保。
    //   2026-07-01: 0.5 → 0.35 (ユーザー実プレイで「もっと速く」 指摘・クライマックス感を強化)。
    //     待ち時間 287-543ms 相当 = 息を継ぐ暇なし・攻撃の選択がひっきりなし。
    finalRoomTempoMul: 0.35,
  },
  {
    // 第4ボス「請われぬ介錯」（旧・常打ちの刑吏／処刑人）。
    //   2026-08-12: 見た目は旧へ戻し、物語は新を維持。技は刃5＋砲3へ絞る (当時の呼び名は大槌。08-18 に刃へ)。
    //   ⚠️ 表示名だけの改名。id='the-executioner' はセーブ互換のため不変。
    //   執着の核 = 「無感覚な暴力の惰性・止め方を忘れた手」(✅生存・台帳 = docs/decision-ledger.md §処刑人)。
    //
    //   2026-08-12 の現行裁定は「旧処刑人の見た目へ戻す／物語は維持／刃5＋砲3」(当時の呼び名は大槌。08-18 に刃へ)。
    //   7月の白紙化・3武器案は退避履歴であり、現行仕様として読まない。
    //   正本 = docs/candidates/2026-08-12-executioner-revert-8moves-transform-ticket.md
    id: 'the-executioner',
    // 🔵 2026-09-02 夜 等身 1.6 化で 0.8 倍（旧 63）
    bodyHalfWidthPx: 50,
    // 🔵 2026-09-06 芯の実寸化 (設計書 §3・便1): 刃=34 (idle 尻帯 68.0/2・人型は尻帯が正)。
    //   砲=56 (弾の生まれる位置からの逆算・設計書 §2-3。胴 36 より広いのは「砲形態は腕が武器でなく体」だから)。
    //   出所 = docs/candidates/2026-09-06-boss-core-real-size-fable-design.md §3。他ボスは 28 のまま (便2以降)。
    coreHalfWidthPxByForm: { blade: 34, cannon: 56 },
    // 重なり (2026-10-06 重なり監査・ユーザー裁定 Q1/Q2。介錯は「立て直しの帯」だけ・止まり位置 ① は入れない＝Q3 の対象外)。
    //   刃の芯の帯は 49px (34+15)。ボスが自分の踏み込みで 49px に止まると従来の立て直し (「より近い」時だけ) が動かず居座った。
    //   帯 52 = 体が触れる距離 T (刃)。ときどき (40%) 次の待機で歩いて下がる。砲形態は芯の帯 71 のままで影響なし。
    //   出所 = docs/research/2026-10-06-overlap-audit/stage2-triage-and-design.md。旧 = DEV ?regainBand=off。
    overlapRegainBandPx: 52,
    backAttackThresholdMs: 100,
    meleeGuard: 'weapon',   // 処刑人: 刃で受ける (旧 = 大斧→大槌→刃・2026-08-15)
    name: '請われぬ介錯',
    title: 'The Standing Headsman', // 英名は未定。配布版は全ボスで英題を描かない (10-18 作者「全ボス、ボス名だけにしてほしい」・render.ts bossTitleShown は開発版だけ) ので、決まるまでこのまま
    role: '王城の処刑場で、止め方を忘れた手が動き続ける',
    // ⚠️ ここは v1 の素の値。**実効プールは econ.bossPools が上書きする**
    //   (実効値は docs/generated/econ-current-values.md を参照・⬜暫定)。数値の議論は econ.ts 側を見ること。
    maxHp: 150,             // 🔴 旧「3幕化 (2026-07-06 技がリッチなので致命3回)」は 2026-08-06 ユーザー裁定「刑吏は2幕」で上書き済み。数値はプレイで詰める
    maxPosture: 120,
    roomCount: 2,   // 幕 = 致命2回 (2026-08-06 ユーザー裁定「刑吏は2幕」)
    roomHpFractions: [40, 60],   // 🟨仮値。確定値はプレイとボット計測で決める (2026-08-06)
    // 技9本 = 刃5＋砲3＋お仕置き1 (砲身薙ぎ exec_cannonSweep = EXECUTIONER_PUNISH_ATTACK_IDS)。
    //   実際に出るのは現在の形態だけで、幕1は刃固定。
    //   ここは全数の宣言 (ロースター整合テスト・検収ツールが読む)。G11 は roomEntryForcedAttacks。
    baseAttackIds: EXECUTIONER_ATTACK_IDS,
    signatureAttackId: 'exec_maulDrop',
    finisherAttackId: 'executioner_finisher',
    // 🔵 2026-09-02 夜 等身 1.6 化で 0.8 倍（旧 130）
    finisherAnchorOffset: 104,
    walkSpeedMul: 0.7,    // 重い足取り
    // 🔴 刃形態の値。実際に効くのは executioner_weapon.ts の EXECUTIONER.ranges (砲形態と
    //   ペアで持つため、rangeTuningFor が先に返る)。**ここだけ直しても何も起きない。**
    //   両方を同じ値に保つ機械ガード = tests/executioner_weapon.test.ts「2か所のズレを止める」。
    // 🔵 2026-09-02 夜 等身 1.6 化で 0.8 倍（旧 170/190）
    preferredRange: 136,
    attackCommitRange: 152,   // range(160) より内側 = 攻撃が確実に届く (2026-07-02 実プレイ調整)
    // ⚠️ 応手は現在の形態に関係なく固定id。骨格C (踏み込み) の刃版を使う。
    //   武器連動にすると、殴られ続けた時の答えが道具で変わってしまい理不尽になる (応手だけは常に同じ)。
    retaliationAttackId: 'exec_maulJab',   // 居座りすぎへの応手
    backAttackId: 'exec_maulJab',          // 背面滞在への応手
    // 幕2開幕の合図 = G11 (2026-07-15移行: HP%駆動→致命回数駆動。 本物のSEKIRO型は各段階が
    //   独立フルバーなのでHP%で「幕2に入った瞬間」を検出できない → 致命1回後=幕2突入の確定初手へ)。
    roomEntryForcedAttacks: [{ room: 1, attackId: 'executioner_phaseTransition' }],
    // 幕による解禁は「骨格」の単位で行う (実体 = executioner_weapon.ts EXECUTIONER_FRAME_UNLOCK)。
    //   ここは同じ内容を技idへ展開したもの = ロースター整合テストと検収ツールが読む鏡。
    //   ⚠️ 幕と武器は独立 (台帳)。同じ幕でも、持っている武器で出る3本が入れ替わる。
    //   幕1 = 落とす + 踏み込む + 地割り (2026-10-02 裁定で地割りを前倒し)
    //   🔴 旧「幕2 = + 溜める / 幕3 = 技は増えず回転だけ上がる」は 2026-08-06 の3幕→2幕裁定で上書き済み。
    //   幕2 = 最終幕。砲 + 長い連を解禁し、同時に finalRoomTempoMul で回転を上げる。
    //     予告尺は縮めない = 公平。長い連を残すことで、体幹で倒し切る道も残す。
    //   ⚠️ 2026-07-30: 骨格が1:多になり、技単位の幕ゲート (atExecutes) も入ったため、
    //      ここで手計算せず executioner_weapon.ts が組んだ鏡をそのまま使う (写すとズレる)。
    unlockByExecutes: EXECUTIONER_UNLOCK_BY_EXECUTES,
    // ★譜面 (2026-07-30 新規)。それまで刑吏には譜面が1本も無く、技9本が全部単発で飛んでいた
    //   (= 弾ける拍が繋がらない = 体幹で倒せない構造)。原因調査 = このセッションの実測。
    //   requiresWeapon で刃/砲の譜面を分ける。
    //   ■ 設計の芯 (ユーザー裁定 2026-07-30)
    //     ・**同じ入り・違う終わり**: 先頭の拍が完全に同じ譜面をペアで置く。プレイヤーは
    //       2〜3拍目まで区別できない = 「神ボス」でよく使われる引っかけ。分岐機構は不要。
    //     ・**不均等な間**: gapMs を揃えない。長い方の譜面には必ず「ワンテンポ」(820〜900ms) を挟む。
    //       重い道具なのでディレイが理不尽にならない (ユーザー「軽い武器のディレイが理不尽なだけ」)。
    //     ・**長い連ほど締めの硬直を深く** (recoveryOverrideMs) = 最後まで捌き切った報酬。
    //     ・**赤は減らさず出現確率で調整する**。完成2体は幕1が赤50〜64%で、幕が進むほど下がる。
    //   ⬜暫定: weight は実測 (tests/combat_sim/economy_probe) を見てから詰める。
    strings: ([
      // ===== 刃: 縦に落とす。一撃が重く、振り切った後が深い =====
      // 🔵 2026-09-06 刃形態のテンポ (Phase 1・docs/candidates/2026-09-05-executioner-blade-tempo-fable-design.md §3-1):
      //   「規則拍」の gapMs を 480→260 へ詰めた。「ワンテンポ」役 (900/780) は不変＝規則拍が速くなることで
      //   初めて聞き分けられる差になる (比 1.14〜1.4 → 1.37〜1.74)。実行時つまみ = setExecutionerBladeGapMs
      //   (?bladeGap= / __setBladeGap)。既定 260・対象は下の6箇所だけ (900/780 は対象外)。
      { id: 'exec-maul-drop-jab', requiresWeapon: 'blade', weight: 1.2, beats: [
        { attackId: 'exec_maulDrop' },
        { attackId: 'exec_maulJab', gapMs: 260, chargeOverrideMs: 420 },
      ] },
      // ↑と先頭が同じ。振り下ろしを見た時点では、次が赤の突きか もう一発の振り下ろしか分からない。
      { id: 'exec-maul-drop-drop-jab', requiresWeapon: 'blade', weight: 1.0, beats: [
        { attackId: 'exec_maulDrop' },
        { attackId: 'exec_maulDrop', gapMs: 260, chargeOverrideMs: 650 },
        // 🔴 3拍目の900は「ワンテンポ」の印そのもの。Phase 1では触らない (上のコメント参照)。
        { attackId: 'exec_maulJab', gapMs: 900, chargeOverrideMs: 420, recoveryOverrideMs: 1150 },
      ] },
      // 密着咎め: 懐に居座ると赤の突きが2連で来る (弾けない = 離れるしかない)。
      { id: 'exec-maul-jab-jab', requiresWeapon: 'blade', weight: 0.6, preferClose: true, beats: [
        { attackId: 'exec_maulJab' },
        { attackId: 'exec_maulJab', gapMs: 260, chargeOverrideMs: 380 },
      ] },
      // ===== 退避ブロックここまで / 以下は現役の刃譜面 =====

      // ===== 幕2 (最終幕): 「腰を落として溜める」+ 長い連を解禁。ここから弾ける連撃が増える =====
      // 🔵 2026-09-06 刃形態のテンポ Phase 1: 2拍目 (規則拍) だけ 520→260。3拍目の780は
      //   「ワンテンポ」の印そのものなので不変 (docs/candidates/2026-09-05-executioner-blade-tempo-fable-design.md §3-1)。
      // 🔵 2026-10-02 ユーザー裁定 (通しパック P-1「幕1が薄い」→ 地割り＋発勁を幕1へ前倒し): unlockAtExecutes を外した。
      //   戻し方 = この連と exec-maul-quake-fajin に unlockAtExecutes: 1 を戻し、boss.ts の floorFajinId の
      //   地割り側へ executesTaken >= 1 を戻す。
      { id: 'exec-maul-drop-quake', requiresWeapon: 'blade', weight: 1.3, beats: [
        { attackId: 'exec_maulDrop' },
        // 地割りの溜めは単発 (exec-maul-quake-fajin) と同じ 1500ms にそろえた (2026-10-10 ユーザー「1.5秒に揃える」)。
        //   旧は chargeOverrideMs: 900。戻す = DEV ?maulQuakeCharge=old (src/exec_quake_charge.ts)。
        { attackId: 'exec_maulQuake', gapMs: 260 },
        { attackId: 'exec_maulDrop', gapMs: 780, chargeOverrideMs: 700, recoveryOverrideMs: 1200 },
      ] },
      // 発勁の突き (2026-09-07 便1)。地割りの直後にだけ繋がる専用譜面 (逐語 §1「榴弾と地割れの直後だけ入れたい」)。
      //   2026-10-02 地割りと一緒に幕1へ前倒し (上の連のコメント参照)。
      //   正本 = docs/candidates/2026-09-07-executioner-fajin-thrust-motion-spec.md
      { id: 'exec-maul-quake-fajin', requiresWeapon: 'blade', weight: 1.1, beats: [
        { attackId: 'exec_maulQuake' },
        { attackId: 'exec_bladeFajin', gapMs: 600 },
      ] },
      // ===== 退避ブロックここまで / 以下は現役の刃譜面 =====

      // ===== 相乗り新技4本の譜面 (2026-07-30/08-04) =====
      // 票 = docs/candidates/2026-07-30-executioner-three-new-attacks-ticket.md
      // 狙い = 「回避して反撃する」リズムを刑吏に持たせる (赤の連撃が1本も無かった)。
      // ⬜ weight は暫定。economy_probe の実測で詰める。

      // 両幕 (幕1から): 据えの一打で「間」を教える。3秒の溜めは殴りに行く時間そのもの。
      { id: 'exec-maul-set', requiresWeapon: 'blade', weight: 1.0, beats: [
        { attackId: 'exec_maulSet' },
      ] },
      // ディレイ技。振り下ろしで拍を体に入れさせ、2拍目は「据える→半歩引く→振り上げる」で狙いを伝える。
      //   正本 = docs/candidates/2026-08-06-executioner-aim-charge-redesign.md
      //   🔴 2拍目に chargeOverrideMs を書かない (書くと 1650ms が上書きされてディレイが消える)。
      // ✅ **2026-08-05 実機判定済み → weight を素の 1.0 へ戻した**（検収用の一時値 2.6 は役目終了）。
      //   絞り込み `__soloString('exec-maul-drop-hold')` で見る方法が確立したので、
      //   重みを盛って通しプレイに出す必要がなくなった。
      // 🔴 旧「overhead_hang を入れれば完成／347ms分岐」は却下済み。
      //   新2キーの当て絵ゲート中で、完成判定は boss-motion-lab.html のユーザー実機確認で行う。
      // 🔵 2026-09-06 刃形態のテンポ Phase 1: 2拍目 480→260 (docs/candidates/2026-09-05-executioner-blade-tempo-fable-design.md §3-1)。
      { id: 'exec-maul-drop-hold', requiresWeapon: 'blade', weight: 1.0, beats: [
        { attackId: 'exec_maulDrop' },
        { attackId: 'exec_maulHold', gapMs: 260 },
      ] },
      // 同じ入り・違う終わり: 振り下ろしの後が「赤の突き」か「3秒溜め」か、入りでは分からない。
      //   (exec-maul-drop-jab / exec-maul-drop-drop-jab と先頭が同じ)
      // 🔵 2026-09-06 刃形態のテンポ Phase 1: 2拍目 860→260 (「同じ入り」を他の刃譜面と揃える。誘いは2.9秒の巻きが担う)。
      { id: 'exec-maul-drop-set', requiresWeapon: 'blade', weight: 0.9, beats: [
        { attackId: 'exec_maulDrop' },
        { attackId: 'exec_maulSet', gapMs: 260, recoveryOverrideMs: 1500 },
      ] },
      // ===== 退避ブロックここまで / 以下は現役の腕砲譜面 =====

      // ===== 腕砲（幕2のみ・数値は attacks.ts と同じく⬜ドラフト） =====
      // 🔵 2026-09-04 譜面の組み直し (実プレイ 2026-09-04 ✅「榴弾を見ながら弾く瞬間が良い」を意図して作る):
      //   榴弾 (撃ち逃げ・着弾 2.0 s 後) の直後に直射/速射を続ける2拍譜面を主にする。
      //   榴弾 700+100+450=1250ms → 間 300 → 直射の溜め 900 → 発射 2450ms → 400px を 0.65 s で着弾 ≈3.1 s。
      //   榴弾の着弾 ≈2.7 s (発射 0.7 s + 予告 2.0 s)。**先に床から逃げ、直後に弾を弾く**＝走ってから構える2段。
      //   速射版は 3発目が ≈4.0 s まで続く＝逃げ→弾く→弾く→弾く。
      //   単発の榴弾 (exec-cannon-mortar) は composed では候補から外す (executionerStringEnabled)。
      //   'single' (比較用・?cannonScore=single) では 09-03 までの単発3本だけ＝2拍譜面を外す。
      //   正本 = docs/candidates/2026-09-04-executioner-cannon-tempo-design.md
      { id: 'exec-cannon-shot', requiresWeapon: 'cannon', weight: 0.9, unlockAtExecutes: 1, beats: [
        { attackId: 'exec_cannonShot' },
      ] },
      { id: 'exec-cannon-volley', requiresWeapon: 'cannon', weight: 0.8, unlockAtExecutes: 1, beats: [
        { attackId: 'exec_cannonVolley' },
      ] },
      { id: 'exec-cannon-mortar', requiresWeapon: 'cannon', weight: 1.0, unlockAtExecutes: 1, beats: [
        { attackId: 'exec_cannonMortar' },
      ] },
      // 榴弾後は直射・速射・発勁を毎回各1/3。
      // 🔵 2026-10-02 ユーザー裁定 (AI点検の候補7「砲形態が榴弾ばかり」→「榴弾の出る率を下げる」):
      //   3連携の重み 各1.1 (合計3.3・砲の技選びの約66%) → 各0.4 (合計1.2・約41%)。直射0.9・速射0.8 は据え置き。
      //   戻す＝下の3本の weight を 1.1 へ。
      { id: 'exec-cannon-mortar-shot', requiresWeapon: 'cannon', weight: 0.4, allowRepeat: true, unlockAtExecutes: 1, beats: [
        { attackId: 'exec_cannonMortar' },
        { attackId: 'exec_cannonShot', gapMs: 300 },
      ] },
      // Adopted 09-17: punish distance after mortar; approaching cancels this follow-up.
      { id: 'exec-cannon-mortar-fajin', requiresWeapon: 'cannon', weight: 0.4, allowRepeat: true, unlockAtExecutes: 1, beats: [
        { attackId: 'exec_cannonMortar' },
        // Read the response position before impact, preserving the original follow-up tempo.
        // 700ms charge + 100ms active + 600ms gap, then fajin anticipation.
        { attackId: 'exec_cannonFajin', gapMs: 600 },
      ] },
      { id: 'exec-cannon-mortar-volley', requiresWeapon: 'cannon', weight: 0.4, allowRepeat: true, unlockAtExecutes: 1, beats: [
        { attackId: 'exec_cannonMortar' },
        { attackId: 'exec_cannonVolley', gapMs: 300 },
      ] },
    ] satisfies BossString[]),
    finalRoomTempoMul: 0.78,   // 幕2=最終幕: 技と長い連の解禁と同時に攻撃の回転だけ上げる。⬜暫定
    // 🔴 loreFragments はここに書かない。main で機構ごと撤去済 (commit 1b8697f・表示経路が無かった)。
    //   文面は退避済み。復活させるなら先に表示経路を作る。
  },
  {
    // ★主役級「覚醒獣」(ゲームに出す名前はこれだけ。注釈・古い記録の「覚醒王」も同じこのボス＝docs/ledger/bosses.md「ボスの名前の正本」)= 王 (dream-hound) の暴走再演。 卒業試験「同じ読みを、速く」+苛烈型
    //   (2026-07-04 企画監修ゲート通過・台帳ピン)。 2026-07-07 Fable5 ビルドで strings 主体へ拡張:
    //   体験の核 = 「苛烈な連の "どこに穴があるか" を解読し、僅かな隙に差し込むギリギリの詰め」。
    //   罠2種 (伸び/追い噛み) が偽物の穴を出題し、本物の穴 (突進 recovery 等) の価値を吊り上げる。
    //   ⚠️数値=全部ドラフト (検収パックで実測)。 設計正本 = docs/reports/2026-07-07-awoken-king-fable5-build-state.md
    id: 'crowned-king-awoken',
    meleeGuard: 'none',   // 覚醒王 (獣の系譜): 受けない
    contactMaterial: 'flesh_chain',
    name: '覚醒獣',
    title: 'The Beast, Awoken',
    role: '解放されたはずの獣に残った、王の役の残骸',
    maxHp: 150,           // 実効プールは econ.ts の bossPools が上書き (実効値は docs/generated/econ-current-values.md・⬜暫定)
    maxPosture: 100,
    roomCount: 3,   // 幕 = 致命3回
    // baseAttackIds は strings 未定義時のフォールバック+検収ツール用の全技一覧 (戦闘の実選択は strings)。
    baseAttackIds: ['feralBite', 'feralBiteDelayed', 'feralRush', 'feralRushChase',
      'rampage', 'feralFrenzy', 'feralFrenzyExtend', 'feralWallDash', 'feralWallDashBack'],
    signatureAttackId: 'feralRush',
    finisherAttackId: 'kingExecutionAwoken',
    // 体の半幅 + 約60。絵を差し替えたら bodyHalfWidthPx と一緒に見直す。
    finisherAnchorOffset: 215,
    bodyHalfWidthPx: 157,
    walkSpeedMul: 1.15,   // ★全ボス最速。 騎士/処刑人と真逆の圧 (台帳ピン)
    // 立ち位置 (2026-08-10 ユーザー実プレイ「基本距離が近すぎてよく見えていない。ボスの顔あたりと
    //   プレイヤーが被る」)。 覚醒王は四つ足で横に長く、頭が前へ大きく張り出すので、共通既定の
    //   preferredRange 150 だとプレイヤーがたてがみの中に立つ = 予兆の構えが自分の体で隠れる。
    //   🔴 この2つは必ずセットで動かす: preferredRange < attackCommitRange でないと、
    //      詰め切った位置が「攻撃を出してよい間合い」の外になり、ボスが一切攻撃しなくなる。
    //   必要量の実測 = 獣の頭 (たてがみ込み) は中心から前へ約150px 張り出し、プレイヤー半幅が15px。
    //      旧 150 も、途中で試した 175 も**隙間が 10px 前後**しか無く「顔の中に立っている」状態だった。
    //   🔴 **attackCommitRange は大暴れの床の帯と連動する。** 帯の奥に立った人が壁際で逃げ切れなく
    //      なるため、逃走に必要な距離 = (commitRange+10) - (offset - width/2) + 15 ≤ 140
    //      (tests/hazard_escape)。**片方だけ動かすと必ず落ちる。**
    //      2026-08-10 は帯の offset を 170→195 へ 25px 前へずらして天井を 215 まで上げた
    //      (ボスが下がったぶん帯も下げた = プレイヤーから見た帯の位置と太さは不変)。
    //   ⬜暫定 (実機で体感調整中)。攻撃の届きは机上では成立 (噛み range130+踏み込み150 / 連噛み
    //      range135+130 / 致命 range140+140 = いずれも踏み込み後に届く)。
    preferredRange: 200,
    attackCommitRange: 210,
    finalRoomTempoMul: 0.6,   // 幕3=クライマックス: 攻撃の回転だけ上げる (予告は縮めない)。⬜ドラフト
    // 「間」= 連と連の間に呼吸を作る (2026-07-07 パックA-7「ずっと忙しい・アイドル短すぎ」対応)。
    //   実効 idle ≈ 420〜980ms (幕3は ×0.6)。 間に殴り続けると retaliation (暴噛) が刺す。⬜ドラフト
    //   2.0→2.8 (07-07 第2回プレイ A-7/#10「一息つけない・体幹回復の間がない」対応=息継ぎを広げる)。
    idleGapMul: 2.8,
    retaliationAttackId: 'feralBite',   // 無心の連打への応手 = 最速の噛み (§4.1)
    backAttackId: 'feralBite',          // 裏取り滞在への応手 (enterState が facing を直す)
    // ⚠️ 配布版 (09-21〜) は src/awoken_rampage_v5.ts が下の並びを差し替える: 幕3の入りの咆哮→大暴れ第5案・ak-rampage は幕3だけ・
    //   ak-wall-rush と ak-bite-bite-rush は幕2へ前倒し・回転上げも幕2から。下の咆哮と幕の並びは旧の形 (DEV ?rampage=old)。
    // 幕3開幕の合図 = 軋む咆哮 (処刑人 G11 同型・2026-07-15移行: HP%駆動→致命回数駆動。
    //   本物のSEKIRO型は各段階が独立フルバーなのでHP%では「幕3に入った瞬間」を検出できない
    //   → 致命2回後=幕3突入の確定初手へ)。
    roomEntryForcedAttacks: [{ room: 2, attackId: 'feralRoar' }],
    // 幕別プール (strings の unlockAtExecutes と鏡合わせ。 roster 整合テスト+検収ツールが読む)。
    unlockByExecutes: [
      { atExecutes: 0, ids: ['feralBite', 'feralFrenzy', 'feralRush'] },
      { atExecutes: 1, ids: ['feralBiteDelayed', 'feralFrenzyExtend', 'feralRushChase', 'rampage'] },
      { atExecutes: 2, ids: ['feralWallDash', 'feralWallDashBack'] },
    ],
    // ===== 連 (string) 構成 = このボスの本体 =====
    //   幕1: 基本文法の高速再出題 (bite / 2連bite / frenzy3 / rush=本命の穴)。
    //   幕2: 罠と外し (伸び=至近で出やすい / 追い噛み=低頻度 / 溜め / rampage / bite→frenzy 押し込み)。
    //   幕3: 最大出力 (壁跳ね往復 / 弾き弾き→赤switch) + tempo 0.6。
    //   分岐は観察可能な条件に紐づける: 伸び=preferClose (張り付き弾きへの出題)・
    //   罠2種の頻度は weight で低め (ヒアリング「攻め時が消える理不尽」回避)。
    strings: [
      // ⚠️ ゾーン補正 (preferMid/Far) は覚醒王では使わない (2026-07-07 独立レビューP1):
      //   攻撃選択は dist ≤ attackCommitRange でしか発火せず preferredRange まで詰めて止まるため、
      //   Far(>280) は実質届かない=付けると ×0.15 が常時かかり「主教材の赤 (rush)」が
      //   近接プレイで構造的に出なくなる。
      //   🔴 2026-08-10 更新: 立ち位置を 150/185 → 200/210 へ広げた (上の preferredRange 参照)。
      //     ①Mid(>140) は届くようになった (が、使う判断はしていない=据え置き)
      //     ②**preferClose(≤140) はボスからは踏まなくなった** = プレイヤーが自分から 60px 詰めた時だけ成立。
      //       伸び (ak-frenzy-extend) の出やすさが下がる方向。⬜ 実機の頻度で再判定する。
      // --- 幕1 (常時) ---
      { id: 'ak-bite', weight: 1.0, beats: [{ attackId: 'feralBite' }] },
      { id: 'ak-bite-bite', weight: 0.85, beats: [
        { attackId: 'feralBite' },
        // 締めの隙 900 (700→・2026-07-07 A-7「メリハリがない」): 連が長いほど深い呼吸を返す
        { attackId: 'feralBite', gapMs: 260, chargeOverrideMs: 380, recoveryOverrideMs: 900 },
      ] },
      { id: 'ak-frenzy', weight: 0.9, beats: [{ attackId: 'feralFrenzy' }] },
      { id: 'ak-rush', weight: 1.0, beats: [{ attackId: 'feralRush' }] },
      // --- 幕2 (executes 1) ---
      //   伸び: preferClose = 張り付いて受け続ける欲張り位置でだけ出やすい (観察可能な条件)。
      //   weight 0.45 = 至近の実効 (×1.8=0.81) でも本物 frenzy (0.9) を超えない (罠<本物の維持)。
      { id: 'ak-frenzy-extend', weight: 0.45, preferClose: true, unlockAtExecutes: 1,
        beats: [{ attackId: 'feralFrenzyExtend' }] },
      { id: 'ak-rush-chase', weight: 0.4, unlockAtExecutes: 1,
        beats: [{ attackId: 'feralRushChase' }] },
      { id: 'ak-bite-delay', weight: 0.5, unlockAtExecutes: 1, beats: [{ attackId: 'feralBiteDelayed' }] },
      { id: 'ak-rampage', weight: 0.7, unlockAtExecutes: 1, beats: [{ attackId: 'rampage' }] },
      { id: 'ak-bite-frenzy', weight: 0.7, unlockAtExecutes: 1, beats: [
        { attackId: 'feralBite' },
        { attackId: 'feralFrenzy', gapMs: 240, recoveryOverrideMs: 1200 },   // 長い連の締め=最深の呼吸
      ] },
      // --- 幕3 (executes 2) ---
      { id: 'ak-wall-rush', weight: 0.65, unlockAtExecutes: 2, beats: [
        { attackId: 'feralWallDash' },
        { attackId: 'feralWallDashBack', gapMs: 80 },
      ] },
      // 弾き→弾き→赤 (回避switch): 手癖の連続パリィを赤で断ち切る局所出題。
      //   rush の telegraph 700ms+danger SE = 切替の反応時間は十分 (T4条件aの実機検証対象)。
      { id: 'ak-bite-bite-rush', weight: 0.55, unlockAtExecutes: 2, beats: [
        { attackId: 'feralBite' },
        { attackId: 'feralBite', gapMs: 260, chargeOverrideMs: 380 },
        { attackId: 'feralRush', gapMs: 320, recoveryOverrideMs: 1000 },   // 3拍連の締め=深い呼吸
      ] },
    ],
  },
  {
    // 第5ボス「終わらぬ鬨」(鶏の獣人)。 旧「倒錯の道化＝あべこべ」を作り直し
    //   (裁定C「弾けない=赤」とあべこべが衝突したため・2026-06-13)。
    //   一戦 (2026-10-29 から配布版) = 技5本・2幕・専用のとどめ「終幕の鬨」。部屋の構成と連は src/rooster_fight.ts、
    //     縦の技の台本は src/rooster_vertical.ts、絵は src/rooster_crow_art.ts。下の定義はその一戦と同じ形にしてある。
    //   前の道化の版 (鉤爪の連 短・長・羽刃の雨・急降下突き・裏へ回る blink) は LUNAIRE-notes へ退避した
    //     (archived/2026-10-29-rooster-jester-version/README.md)。
    //   id=inverted-jester 据え置き (rename は将来の一斉リネーム回で実装相談)。
    id: 'inverted-jester',
    meleeGuard: 'none',   // 鶏=獣 (鉤爪)・金属武器で受けない (王 dream-hound と同じ none)
    name: '終わらぬ鬨',
    title: 'L\'Aube Sans Fin',
    role: '夜明け＝幕引きを告げる役。鳴けば終われると信じ、来ぬ朝へ鬨を上げ続ける獣',
    maxHp: 110,
    maxPosture: 95,
    roomCount: 2,   // 体力・幕の取り分は econ.ts の bossPools (10-31 = 180・45:55)
    baseAttackIds: ['tokiZurashiShort', 'tokiZurashiLong', 'roosterDawnCall'],
    signatureAttackId: 'tokiZurashiShort',
    finisherAttackId: 'crowExecution',
    finisherAnchorOffset: 120,
    walkSpeedMul: 0.95,
    unlockByExecutes: [
      // 1幕 = 鬨ずらし 短・長 (鳴き止みを見て爪を弾く)・暁呼び (宙から羽の雨・降りて翼打ち)。
      { atExecutes: 0, ids: ['tokiZurashiShort', 'tokiZurashiLong', 'roosterDawnCall'] },
      // 2幕 = 宙廻り・鳴き挟み (爪2発 → 真上へ → 落ちる踏みつけ) が加わる。
      { atExecutes: 1, ids: ['roosterAerialRoam', 'roosterClawPair', 'roosterCrowDrop'] },
    ],
  },
  {
    // 鶏の次 = 軍配者たち。 11-03 に剣舞と順を入れ替えた (作者「千日手の軍配者は、ラストボス手前のボスって感じでは…ない」)。
    //   下の前の版 (総集編) は剣舞の後に来る前提のまま (終幕の waltzFlurry・台本の拍手の1行)。 比べ用なので直していない。
    // hollow-actor の前の版 (総集編・2026-07-04)。 配布版は 10-31 から src/twins_fight.ts の「千日手の軍配者たち」で、
    //   名前・題・部屋の数はそちらが一戦の間だけ書き換える。 ここの技の並びは開発版 &twins=old の比べ用。
    //   主題 = 総集編。 部屋ごとに過去ボスの「役」を演じ直す: 王 → 花嫁 → 騎士 → 全部混ぜ。
    //   unlockMode 'replace' で部屋=演目を丸ごと差し替える。 描画も演目の元ボスの姿を借りる。
    //   プレイヤーにとっては「ここまでの全文法の再試験」+ 既知の読みが混ざる最終問題。
    id: 'hollow-actor',
    // meleeGuard 未指定 (=none)。
    name: '千日手の軍配者たち',
    title: 'The Sennichite Tacticians',
    role: '自分の役を持たず、他人の役だけを完璧に演じる役者',
    maxHp: 180,
    maxPosture: 100,
    roomCount: 4,   // 部屋 = 45HP ×4 = 演目4幕
    baseAttackIds: ['houndBite', 'houndLunge'],   // フォールバック (第1幕と同じ)
    signatureAttackId: 'houndLunge',
    finisherAttackId: 'hollowExecution',
    finisherAnchorOffset: 125,
    unlockMode: 'replace',
    unlockByExecutes: [
      { atExecutes: 0, ids: ['houndBite', 'houndLunge'] },                    // 第1幕: 王
      { atExecutes: 1, ids: ['veilSweep', 'veilInvitation', 'vowEmbrace'] },  // 第2幕: 花嫁
      { atExecutes: 2, ids: ['lanceThrust', 'lanceSweep'] },                  // 第3幕: 騎士
      { atExecutes: 3, ids: ['houndLunge', 'veilSweep', 'lanceThrust', 'waltzFlurry'] }, // 終幕: 混演
    ],
  },
  {
    // 先代の前 = 剣舞の役者 (11-03 に軍配者たちと順を入れ替えた・難しさと戦いの釣り合いから)。
    // 「剣舞の役者」— 企画監修ゲート通過 (2026-07-04 ユーザー裁定・台帳参照)。
    //   主題 = リズム。 連続弾きの気持ちよさを最大化する。
    //   体幹経済 = 幻影破戒僧型 (台帳確定): HP を削るまで体幹が爆速回復して溜まらず、
    //     弾き続けながらちょこちょこ削り、HP がラインを越えると体幹がしっかり溜まり出す。
    //     ✅ 2026-07-30: per-boss の**体幹回復速度**は実装済 = `econ.bossPools[].postureRecoveryPerSec`
    //       (刑吏が 9/秒 で初採用)。剣舞の制作時はここに数値を入れるだけでこの型が作れる。
    //       ⚠️ ゲイン床の per-boss override は引き続き未実装。
    //       台帳 = docs/decision-ledger.md §常打ちの刑吏「2026-07-30 セッション」
    //   🔴廃止 (2026-07-28 記録棚卸し): ~~「体幹 90 = 全ボス最少。長い連舞を弾き切れば即崩し級」~~
    //     = 台帳で「体幹最少で即崩れ案は破棄」と裁定済み。実値も econ.bossPools で 130。
    //     このコメントだけが古い前提のまま残っていた。
    id: 'blade-dancer',
    // 2026-10-19 B の立ち絵を 186px で測った体の半幅 (胴と冠。手の剣は除く)。60 以下なので届く距離は変わらない。
    bodyHalfWidthPx: 57,
    meleeGuard: 'weapon',   // 剣舞: 双剣で受ける
    name: '剣舞の役者',
    title: 'The Blade Dancer',
    role: '拍手の代わりに剣戟の音を集める踊り手',
    maxHp: 150,
    maxPosture: 90,
    roomCount: 3,   // 部屋 = 50HP ×3。 3拍子 → 旋回 → 五連と舞が深まる
    baseAttackIds: ['waltzFlurry', 'pirouette', 'hemSweep', 'crescendo'],
    signatureAttackId: 'waltzFlurry',
    finisherAttackId: 'dancerExecution',
    finisherAnchorOffset: 125,
    unlockByExecutes: [
      { atExecutes: 0, ids: ['waltzFlurry', 'pirouette'] },
      { atExecutes: 1, ids: ['hemSweep'] },
      { atExecutes: 2, ids: ['crescendo'] },
    ],
  },
  {
    // ★ラスボス「先代の姫」— 企画監修ゲート通過 + ステージ1設計監修通過
    //   (2026-07-04 / 2026-07-11 ユーザー裁定)。 ここで初めて isFinale が正位置に着く。
    //   🔴廃止 (2026-07-11 鏡コンセプト白紙化・2026-07-28 記録棚卸しで発見):
    //     ~~「主題 = 鏡。君と同じ背格好・同じ術理で戦う」「部屋1: 鏡映の決闘 (君の斬撃と突きの写し)」~~
    //     台帳で「動きの鏡 = 廃止」と裁定済み。**このコメントだけが廃止前の前提のまま残っており、
    //     実装するAIが台帳より先にここを読んで汚染される状態だった。**
    //   現行コンセプト = 「**完成された上演**」: 何百年も同じ果たし合いを演じ磨き上げた達人。
    //     動きは誰の写しでもない = 役の年月そのもの。レイピアは「役の道具」として維持
    //     (動きの鏡ではなく、君の役の未来を示す物)。
    //   設計の柱: ①五つの構え×十五の答え (判別を構え言語で一元化)
    //     ②見得 = 全技の終わりは決め絵の長保持 (近接 recovery >= 650ms 保証 = 攻め時)
    //     ③誘いの構え (偽の隙 = メタ出題) ④幕2で舞台が牙を剥く位相転換。
    //   設計正本 = docs/candidates/2026-07-11-former-princess-complete-design-v4-blank-slate.md
    //     / 台帳 = docs/decision-ledger.md §先代の姫。 ⚠️数値は全部暫定。
    id: 'former-princess',
    meleeGuard: 'weapon',   // 先代の姫: 全人格の引用=武器で受ける
    name: '先代の姫',
    title: 'The Former Princess',
    role: '君の役を、君の前に演じていた人',
    maxHp: 180,
    maxPosture: 105,
    roomCount: 3,   // 部屋 = 60HP ×3
    baseAttackIds: ['mirrorSlash', 'mirrorThrust', 'mirrorWaltz', 'lightsFall', 'curtainSweep', 'bellToll'],
    signatureAttackId: 'mirrorSlash',
    finisherAttackId: 'princessExecution',
    finisherAnchorOffset: 115,   // 人間サイズ同士の近い刺し
    unlockByExecutes: [
      { atExecutes: 0, ids: ['mirrorSlash', 'mirrorThrust'] },
      { atExecutes: 1, ids: ['mirrorWaltz'] },
      { atExecutes: 2, ids: ['lightsFall', 'curtainSweep', 'bellToll'] },
    ],
    // 物語の正式な幕引き。 撃破で campaign clear → 「残るか、降りるか」へ。
    isFinale: true,
  },
]

// 明示した比較URLだけ。配布ビルドと通常起動には候補を適用しない。
if (import.meta.env.DEV && typeof window !== 'undefined'
  && ['arena', 'reach', 'reachFar', 'reachFarBefore'].includes(new URLSearchParams(window.location.search).get('awokenFinale') ?? '')) {
  const awoken = BOSS_DEFINITIONS.find(boss => boss.id === 'crowned-king-awoken')!
  awoken.strings = buildAwokenFinaleTrial(awoken.strings!)
}

export function getBossDefinition(index: number): BossDefinition {
  const i = Math.max(0, Math.min(BOSS_DEFINITIONS.length - 1, index))
  return resolveKnightActs(resolveHoundKit(BOSS_DEFINITIONS[i]!))
}

export function getBossDefinitionById(id: string): BossDefinition | undefined {
  const def = BOSS_DEFINITIONS.find((b) => b.id === id)
  return def ? resolveKnightActs(resolveHoundKit(def)) : undefined
}

// 実機比較用 (2026-09-06): legacy は幅だけを共通28へ戻す。滑り出しの規則は残す。
export type BossCoreMode = 'real' | 'legacy'
let bossCoreMode: BossCoreMode = 'real'
export function setBossCoreMode(mode: BossCoreMode): void {
  if (mode !== 'real' && mode !== 'legacy') return
  bossCoreMode = mode
}

// ボスの「芯」の半幅を解決する唯一の入口 (2026-09-06 便1・設計書 §5-1)。
//   player.ts (歩き・見切り突進・§4の滑り) / boss.ts (clampToBossCoreFront) / render.ts (H表示) /
//   テストの全部がこれだけを読む。BOSS_CORE.coreHalfWidth を直接読む箇所を残さない。
//   優先順位: 変形の形態別上書き (coreHalfWidthPxByForm) > ボス固有値 (coreHalfWidthPx) > 共通既定 (28)。
export function bossCoreHalfWidth(boss: { id: string; exec?: { held: ExecutionerHeld } }): number {
  if (bossCoreMode === 'legacy') return BOSS_CORE.coreHalfWidth
  const def = getBossDefinitionById(boss.id)
  if (!def) return BOSS_CORE.coreHalfWidth
  if (boss.exec && def.coreHalfWidthPxByForm) {
    const byForm = def.coreHalfWidthPxByForm[boss.exec.held]
    if (byForm !== undefined) return byForm
  }
  return def.coreHalfWidthPx ?? BOSS_CORE.coreHalfWidth
}

// 見切り反撃・連撃3段目の踏み込みの止まり位置 (ボス中心との距離 px・0 = 止めない)。player.ts moveForwardStoppingAtCore が読む。
export function bossCounterStopGap(boss: { id: string }): number {
  return getBossDefinitionById(boss.id)?.lungeStopGapPx ?? 0
}

// 🔵 2026-09-06 刃形態のテンポ (docs/candidates/2026-09-05-executioner-blade-tempo-fable-design.md §3-1):
//   刃譜面の「規則拍」(gapMs) だけを実行時に書き換えるつまみ。実装作法は setExecutionerDownswingChargeMs
//   (executioner_weapon.ts) と同型＝範囲チェック→対象の mutable フィールドを直接書き換える。
//   ここでは BOSS_DEFINITIONS の実オブジェクトを直接書き換える (strings は as const で凍結していない)。
//   🔴 対象は下の6箇所だけ。「ワンテンポ」の印 (drop-drop-jab 3拍目の900・drop-quake 3拍目の780) は
//   触らない＝設計の核 (規則拍が速くなって初めて聞き分けられる差になる) が消えるため。
const EXECUTIONER_BLADE_GAP_TARGETS: ReadonlyArray<{ stringId: string; beatIndex: number }> = [
  { stringId: 'exec-maul-drop-jab', beatIndex: 1 },
  { stringId: 'exec-maul-drop-drop-jab', beatIndex: 1 },
  { stringId: 'exec-maul-jab-jab', beatIndex: 1 },
  { stringId: 'exec-maul-drop-hold', beatIndex: 1 },
  { stringId: 'exec-maul-drop-set', beatIndex: 1 },
  { stringId: 'exec-maul-drop-quake', beatIndex: 1 },
]
export function setExecutionerBladeGapMs(ms: number): void {
  if (!Number.isFinite(ms) || ms < 1 || ms > 2000) return
  const def = getBossDefinitionById('the-executioner')
  if (!def?.strings) return
  for (const target of EXECUTIONER_BLADE_GAP_TARGETS) {
    const string = def.strings.find((s) => s.id === target.stringId)
    const beat = string?.beats[target.beatIndex]
    if (beat) beat.gapMs = ms
  }
}

// 「最終ボス」= 物語の finale (先代の姫) を明示フラグで判定する。 配列末尾 ≠ finale。
//   実装途中の配役では「最後に実装したボス」が末尾に来るが、 それは finale ではない。
//   → 王 (現在唯一の実装ボス) を倒しても cleared にならず victory のまま (UX 維持)。
//   先代の姫 実装時に def.isFinale=true を立てると、 そこで初めて cleared が成立する。
export function isFinalBossIndex(index: number): boolean {
  const def = BOSS_DEFINITIONS[Math.max(0, Math.min(BOSS_DEFINITIONS.length - 1, index))]
  return def?.isFinale === true
}

// 旧・攻撃継承システムは 2026-06-11 に停止 (Codex監査8 / spec 2026-06-11-bride-evade-boss-design §15)。
//   旧式は「撃破ボスの signature 技を次ボスのプールへ混ぜる」ローグライト継承だったが、
//   王の突進 (houndLunge) が花嫁戦へ混入する潜在バグであり、当時の正典では継承は hollow-actor の前の版
//   1体だけの個性に縮小された。 → 常に固定 baseAttackIds のみ返す。
export function buildAttackPool(currentBossIndex: number, _defeatedBossIds: string[]): string[] {
  return [...getBossDefinition(currentBossIndex).baseAttackIds]
}

// 致命回数フェーズ解禁 (花嫁 spec §8): プレイヤーが致命を入れた回数 (boss.executesTaken) に
//   応じて攻撃プールが増える。 unlockByExecutes 未定義のボスは常に baseAttackIds 全部。
export function attackPoolForBoss(def: BossDefinition, executesTaken: number): string[] {
  if (!def.unlockByExecutes) return [...def.baseAttackIds]
  if (def.unlockMode === 'replace') {
    // 演目切り替え式: 到達済みの最新段階「だけ」を返す (hollow-actor の前の版の部屋=幕)
    let current: string[] = def.unlockByExecutes[0]?.ids ?? def.baseAttackIds
    for (const stage of def.unlockByExecutes) {
      if (executesTaken >= stage.atExecutes) current = stage.ids
    }
    return [...current]
  }
  const ids: string[] = []
  for (const stage of def.unlockByExecutes) {
    if (executesTaken >= stage.atExecutes) ids.push(...stage.ids)
  }
  return Array.from(new Set(ids))
}
